import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import Footer from "../../Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";
import { projectData } from "../../data/projectsData";
export default function ProjectsSection({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  /* projectData imported from ../data/projectsData */

  const categories = Object.keys(projectData);

  const openModal = (categoryId) => {
    setSelectedCategory(categoryId);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
    document.body.style.overflow = 'unset';
  };

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isModalOpen]);

  return (
    <>
      <section className="min-h-screen py-24 md:py-32 bg-[--bg-primary] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[--accent] rounded-full opacity-5 blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500 rounded-full opacity-5 blur-3xl" />
        </div>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          {/* Header */}
          <motion.div variants={itemAnimation} className="text-center mb-16">
            <motion.div className="inline-flex items-center justify-center mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
              <span className="text-[--accent] text-sm font-medium tracking-[0.3em] uppercase mx-6">
                04
              </span>
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
            </motion.div>

            <h2 className="text-4xl md:text-6xl font-display font-bold text-[--text-primary] mb-4 leading-tight">
              {t.projects.title}
            </h2>
            <p className="text-lg text-[--text-secondary]">
              {t.projects.subtitle}
            </p>
          </motion.div>

          {/* Category Grid */}
          <motion.div variants={itemAnimation}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((categoryId, index) => {
                const category = projectData[categoryId];
                return (
                  <motion.div
                    key={categoryId}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -8, scale: 1.02 }}
                    onClick={() => openModal(categoryId)}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-2xl aspect-[4/3] bg-gray-200 dark:bg-gray-800">
                      <img
                        src={category.coverImage}
                        alt={category.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <h3 className="text-2xl font-display font-bold text-white mb-1">
                            {t.projects[category.titleKey]}
                          </h3>
                          <p className="text-white/90 text-sm mb-3">
                            {t.projects[category.subtitleKey]}
                          </p>
                          <div className="flex items-center text-white/80 text-sm">
                            <span>{category.images.length} {t.projects.projectsCount}</span>
                            <span className="mx-2">•</span>
                            <span className="group-hover:text-[--accent] transition-colors">{t.projects.viewMore} →</span>
                          </div>
                        </div>
                      </div>

                      {/* Accent border on hover */}
                      <div className="absolute inset-0 border-2 border-[--accent] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>

        {/* Gradient Footer */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[--bg-secondary] via-[--bg-secondary]/60 to-transparent pointer-events-none" />
      </section>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && selectedCategory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/90 backdrop-blur-sm"></div>

            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-[--bg-primary] rounded-3xl max-w-7xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            >
              {/* Close Button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-[--bg-secondary] hover:bg-[--accent] text-[--text-primary] hover:text-white transition-all duration-300 flex items-center justify-center shadow-lg"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="sticky top-0 bg-[--bg-primary]/95 backdrop-blur-sm border-b border-[--accent]/20 p-6 z-10">
                <h3 className="text-3xl font-display font-bold text-[--text-primary]">
                  {t.projects[projectData[selectedCategory].titleKey]}
                </h3>
                <p className="text-[--text-secondary] mt-1">
                  {t.projects[projectData[selectedCategory].subtitleKey]}
                </p>
              </div>

              {/* Scrollable Image Grid */}
              <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projectData[selectedCategory].images.map((image, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group relative overflow-hidden rounded-xl aspect-square bg-gray-200 dark:bg-gray-800"
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 right-4">
                          <p className="text-white text-sm font-medium">{image.alt}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer
        onNavigate={onNavigate}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
        onOpenCookiePolicy={onOpenCookiePolicy}
        onOpenTermsOfService={onOpenTermsOfService}
      />
    </>
  );
}
